/** @jsxImportSource @emotion/react */
import { jsx, css } from "@emotion/react";
import { Button } from "@mui/material";
import { GroupInfo, GroupMemberInfo } from "../types/gigya";
import { useTheme } from "@table-library/react-table-library/theme";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  DEFAULT_OPTIONS,
  getTheme,
} from "@table-library/react-table-library/material-ui";
import { usePagination } from "@table-library/react-table-library/pagination";
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
  Data,
  TableNode,
} from "@table-library/react-table-library/table";
import { useMobileDetector } from "../hooks";
import { useTranslation } from "react-i18next";
import { AccordionTablePendingItem } from "./AccordianTablePendingItem";
import { GigyaGroupModels } from "../types/gigya.schema";
import { SetStateAction, useEffect, useState } from "react";
import { useGetMembers, useSetMemberInfo } from "../api";

type SEARCHGROUP = {
  UID: string;
  groupId: string;
  model: string;
  relationshipData: {
    role: string;
    status: string;
  };
  profile: {
    lastName: string;
    country: string;
    locale: string;
    email: string;
    zip: string;
    firstName: string;
  };
  memberSince: string;
  lastUpdated: string;
  memberSinceTimestamp: number;
  lastUpdatedTimestamp: number;
  permissions: string;
};

type TableMembersPendingProps = {
  myUID: string;
  groupID: string;
  group: SEARCHGROUP;
};

// IMPLEMENT CONTEXT FOR CURRENT USER, MEMBERS, API METHODS
// IMPLEMENT REACT API
export const TableMembersPending = (props: TableMembersPendingProps) => {
  const { myUID, groupID, group } = props;
  const LIMIT = 5;
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const {
    members,
    setMembers,
    setNewGroupId,
    loadingMembers,
    membersError,
    paginationStart,
    setPaginationStart,
    paginationLimit,
    paginationTotalCount,
  } = useGetMembers(myUID, groupID, "pending");
  const { memberInfoResp, setMemberInfo, loadingMemberInfo, memberInfoError } =
    useSetMemberInfo(myUID, groupID);
  const [dummyMember, setDummyMember] = useState<GroupMemberInfo>();
  const [replaceDummies, setReplaceDummies] = useState(false);
  const [paginatedMembers, setPaginatedMembers] = useState<GroupMemberInfo[]>(
    [],
  );
  const [membersData, setMembersData] = useState<Data<TableNode>>({
    nodes: [],
  });

  const handleApproved = (member: GroupMemberInfo) => {
    const updatedPaginatedMembers = paginatedMembers.filter(
      (pendingMember) => pendingMember.UID !== member.UID,
    );
    setPaginatedMembers(updatedPaginatedMembers);

    // TODO
    // The UI change should only be done after a successful api request
    setMemberInfo([
      {
        memberUid: member.UID,
        status: "approved",
        role: member.relationshipData.role,
        groupId: groupID,
      },
    ]);
  };

  const handleRejected = (member: GroupMemberInfo) => {
    const updatedPaginatedMembers = paginatedMembers.filter(
      (pendingMember) => pendingMember.UID !== member.UID,
    );
    setPaginatedMembers(updatedPaginatedMembers);

    // TODO
    // The UI change should only be done after a successful api request
    setMemberInfo([
      {
        memberUid: member.UID,
        status: "rejected",
        role: member.relationshipData.role,
        groupId: groupID,
      },
    ]);
  };

  const doGet = (params: {
    offset: SetStateAction<number>;
    insertDummies?: boolean;
    replaceDummies?: boolean;
  }) => {
    if (params.insertDummies) {
      // APPAEND ARRAY OF DUMMIES UP TO OFFSET - 1
      const dummiesArr: GroupMemberInfo[] = [];
      const numberOfDummiesNeeded =
        Number(params.offset) - paginatedMembers.length;
      for (let i = 0; i < numberOfDummiesNeeded; i++) {
        dummiesArr.push(dummyMember!);
      }
      setPaginatedMembers([...paginatedMembers, ...dummiesArr]);
      setPaginationStart(params.offset);
    } else if (params.replaceDummies) {
      // REPLACE DUMMIES STARTING AT OFFSET UNTIL
      // MEMBERS ARRAY IS EXHAUSTED
      setReplaceDummies(true);
      setOffset(params.offset);
      setPaginationStart(params.offset);
    } else {
      setPaginationStart(params.offset);
    }
  };

  useEffect(() => {
    // SET INITIAL PAGINATED MEMBERS AND DUMMY MEMBER
    if (members.length && paginatedMembers.length === 0) {
      setDummyMember({ ...members[0], UID: "DUMMY" });
      setPaginatedMembers(members);
    }
    // REPLACE DUMMIES
    else if (members.length && paginatedMembers[offset].UID === "DUMMY") {
      const beginning = paginatedMembers.slice(0, offset);
      const end = paginatedMembers.slice(offset + LIMIT);
      setReplaceDummies(false);
      setPaginatedMembers([...beginning, ...members, ...end]);
    }
    // APPEND PAGE
    else {
      setPaginatedMembers([...paginatedMembers, ...members]);
    }
  }, [members]);

  // UPDATE MEMBERS DATA WHEN PAGINATED MEMBERS IS UPDATED
  useEffect(() => {
    const newMembersData = {
      nodes: paginatedMembers.map((node, index) => {
        return { ...node, id: index };
      }),
    };
    setMembersData(newMembersData);
  }, [paginatedMembers]);

  const onPaginationChange = (action: any, state: any) => {
    const offset = state.page * LIMIT;
    // IF NUMBER OF ITEMS IS GREATER THAN PAGE - 1 * LIMIT
    // AND THERE IS NO DUMMY AT PAGE * LIMIT
    const paginatedMembersArrLen = paginatedMembers.length;
    const newPaginatedMembersArrLen = offset + LIMIT;
    const previousPageArrLen = (state.page - 1) * LIMIT;

    // IF PAGINATED MEMBERS HAS THE CORRECT AMOUNT OF ITEMS
    // BUT A DUMMY VALUE EXIST ON THE PAGE SELECTED, DOGET
    // AND REPLACE THE DUMMY VALUES
    if (
      paginatedMembersArrLen > offset &&
      paginatedMembers[offset].UID === "DUMMY"
    ) {
      // REPLACE DUMMIES
      doGet({
        offset,
        replaceDummies: true,
      });
    }

    // IF PAGINATED MEMBERS + LIMIT IS GREATER THAN THE
    // MAXMIMUM NUMBER OF POSSIBLE MEMBERS OF THE SELECTED
    // PAGE, DO NOTHING. PREVENTS FETCHING+APPENDING
    // MEMBERS ON THE LAST PAGE WHEN WE ALREADY HAVE THEM
    else if (paginatedMembersArrLen + LIMIT > newPaginatedMembersArrLen) {
      // TODO
    }

    // IF PAGINATED MEMBERS HAS THE CORRECT AMOUNT OF ITEMS
    // AND NO DUMMY VALUE EXIST ON THE PAGE SELECTED,
    // RENDER THE NEW PAGE
    else if (
      paginatedMembersArrLen >= newPaginatedMembersArrLen &&
      paginatedMembers[offset].UID !== "DUMMY"
    ) {
      // TODO
    }

    // IF PAGINATED MEMBERS HAS TOO FEW ITEMS, DOGET THE NEW
    // PAGE AND INSERT DUMMY VALUES WHERE NECESSARY
    else if (paginatedMembersArrLen <= newPaginatedMembersArrLen - LIMIT * 2) {
      doGet({
        offset,
        insertDummies: true,
      });
    } else {
      doGet({
        offset,
      });
    }
  };

  const pagination = usePagination(membersData, {
    state: { page: 0, size: 5 },
    onChange: onPaginationChange,
  });

  const customTheme = {
    Table: ``,
    Header: ``,
    Body: ``,
    HeaderCell: ``,
    HeaderRow: `
      font-family: Montserrat;
      font-size: 24px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;

      & .th.border-remove {
        border-bottom: 0;
      }

      &.tr-header th {
        background-color: unset;
        font-weight: normal;
        text-align: unset;
        border: none;
        padding: 0;
      };
    `,
    BaseRow: `
      font-family: Montserrat;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    `,
    Row: `
      &.td {
        border: none;
        padding: 0;
      };

      &.tr-body.team-management td {
        border-top: none;
        border-left: none;
        border-right: none;
        border-bottom: 1px solid #e0e0e0;
        padding: 0;
      };
    `,
    BaseCell: `
      padding: 0;
      margin-bottom: 15px;
      border: 0;
    `,
    Cell: `
      >div {
        white-space: normal;
        word-break: break-all;
      }
    `,
  };

  const theme = useTheme([getTheme(DEFAULT_OPTIONS), customTheme]);
  const { isMobile } = useMobileDetector();

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const renderName = (row: GroupMemberInfo) => {
    return `${row.profile.lastName}, ${row.profile.firstName}`;
  };

  const renderRole = (row: GroupMemberInfo) => {
    return t(`exported.${capitalizeFirstLetter(row.relationshipData.role)}`);
  };

  const renderEmail = (row: GroupMemberInfo) => {
    return row.profile.email;
  };

  const handleReject = (event: React.SyntheticEvent, row: GroupMemberInfo) => {
    handleRejected(row);
  };

  const handleApprove = (event: React.SyntheticEvent, row: GroupMemberInfo) => {
    handleApproved(row);
  };

  const renderApprovalActions = (row: GroupMemberInfo) => {
    return (
      <>
        <Button
          sx={{
            backgroundColor: "#d32f2f",
            ":hover": { backgroundColor: "#ab1d1d" },
            borderRadius: "4px",
            height: "35px",
          }}
          onClick={(event) => handleReject(event, row)}
        >
          {t("reject")}
        </Button>
        <Button
          sx={{
            backgroundColor: "#1976d2",
            borderRadius: "4px",
            height: "35px",
          }}
          onClick={(event) => handleApprove(event, row)}
        >
          {t("approve")}
        </Button>
      </>
    );
  };

  const COLUMNS = [
    {
      label: "Name",
      renderCell: renderName,
    },
    {
      label: "Role",
      renderCell: renderRole,
    },
    {
      label: "Email",
      renderCell: renderEmail,
    },
    {
      label: "",
      renderCell: renderApprovalActions,
    },
  ];

  const renderPaginationButtons = (
    currentPage: number,
    totalPages: number,
    maxVisiblePages: number,
  ) => {
    const visiblePages = [];

    if (totalPages > 0) {
      visiblePages.push({ label: 1, page: 1 });
    }

    // Calculate start and end of the visible range
    let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(
      totalPages - 1,
      currentPage + Math.floor(maxVisiblePages / 2),
    );

    // Adjust start and end page if needed
    if (endPage - startPage + 1 < maxVisiblePages) {
      if (startPage === 2) {
        endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 2);
      } else {
        startPage = Math.max(2, endPage - maxVisiblePages + 2);
      }
    }
    // Add ellipsis and pages in between
    if (startPage > 2) {
      visiblePages.push({
        label: "...",
        page: visiblePages[visiblePages.length - 1].page + 1,
      });
    }
    for (let i = startPage; i <= endPage; i++) {
      visiblePages.push({ label: i, page: i });
    }
    if (endPage < totalPages - 1) {
      visiblePages.push({
        label: "...",
        page: visiblePages[visiblePages.length - 1].page + 1,
      });
    }

    // Add last page
    if (totalPages > 1) {
      visiblePages.push({ label: totalPages, page: totalPages });
    }

    return visiblePages;
  };

  return (
    <>
      {isMobile && (
        <>
          <Table
            data={membersData}
            theme={theme}
            pagination={pagination}
            layout={{ isDiv: true }}
          >
            {(tableList: any[]) => (
              <Body>
                {tableList.map((item) => (
                  <Accordion
                    key={item.id}
                    sx={{
                      mb: 0.5,
                      boxShadow: "none",
                      "&:before": { display: "none" },
                      borderBottom: "0.5px solid #ddd",
                      overflow: "hidden", // Ensure no overflow beyond boundaries
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${item.id}-content`}
                      id={`panel${item.id}-header`}
                      sx={{
                        bgcolor: "#F1F6FB",
                        "& .MuiAccordionSummary-content": {
                          margin: 0,
                        },
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          transform: "rotate(0deg)", // Align icon with summary to prevent overflow
                        },
                        padding: "5px 10px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "1rem",
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        {renderName(item)}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ bgcolor: "#D6E3F0", padding: "5px 10px" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          rowGap: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              flexShrink: 0,
                              marginRight: "8px",
                            }}
                          >
                            {`${t("exported.Role")}:`}
                          </Typography>
                          <Typography sx={{ flexGrow: 1 }}>
                            {renderRole(item)}
                          </Typography>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "15px",
                            marginTop: "8px",
                          }}
                        >
                          <Typography
                            sx={{ fontWeight: "bold", flexShrink: 0 }}
                          >
                            {`${t("exported.Email")}:`}
                          </Typography>
                          <Typography
                            sx={{
                              flexGrow: 1,
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              color: "#1a73e8",
                              textDecoration: "underline",
                            }}
                          >
                            <a
                              href={`mailto:${renderEmail(item)}`}
                              style={{ color: "#1a73e8" }}
                            >
                              {renderEmail(item)}
                            </a>
                          </Typography>
                        </div>
                        <Cell>{renderApprovalActions(item)}</Cell>
                      </div>
                    </AccordionDetails>
                  </Accordion>
                ))}
                {tableList.length === 0 && (
                  <div>There are no pending members</div>
                )}
              </Body>
            )}
          </Table>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "50px",
            }}
          >
            <span>Total Pages: {Math.ceil(paginationTotalCount / 5)}</span>

            <span>
              Page:{" "}
              {renderPaginationButtons(
                pagination.state.page,
                Math.ceil(paginationTotalCount / 5),
                4,
              ).map((node: any, index: number) => (
                <button
                  key={index}
                  type="button"
                  style={{
                    fontWeight:
                      pagination.state.page === Number(node.page - 1)
                        ? "bold"
                        : "normal",
                  }}
                  onClick={() =>
                    pagination.fns.onSetPage(Number(node.page - 1))
                  }
                >
                  {node.label}
                </button>
              ))}
              {membersData.nodes.length === 0 && (
                <button
                  key={0}
                  type="button"
                  style={{
                    fontWeight: "normal",
                  }}
                  onClick={() => pagination.fns.onSetPage(0)}
                >
                  {1}
                </button>
              )}
            </span>
          </div>
        </>
      )}
      {!isMobile && (
        <>
          <Table data={membersData} theme={theme} pagination={pagination}>
            {(tableList: any[]) => (
              <>
                <Header>
                  <HeaderRow>
                    <HeaderCell className="border-remove">
                      {t("exported.Name")}
                    </HeaderCell>
                    <HeaderCell className="border-remove">
                      {t("exported.Role")}
                    </HeaderCell>
                    <HeaderCell className="border-remove">
                      {t("exported.Email")}
                    </HeaderCell>
                    <HeaderCell className="border-remove"></HeaderCell>
                  </HeaderRow>
                </Header>
                <Body>
                  {tableList.map((item) => (
                    <Row className="team-management" key={item.id} item={item}>
                      <Cell>{renderName(item)}</Cell>
                      <Cell>{renderRole(item)}</Cell>
                      <Cell>{renderEmail(item)}</Cell>
                      <Cell>{renderApprovalActions(item)}</Cell>
                    </Row>
                  ))}
                  {tableList.length === 0 && (
                    <div>There are no pending members</div>
                  )}
                </Body>
              </>
            )}
          </Table>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "50px",
            }}
          >
            <span>Total Pages: {Math.ceil(paginationTotalCount / 5)}</span>

            <span>
              Page:{" "}
              {renderPaginationButtons(
                pagination.state.page,
                Math.ceil(paginationTotalCount / 5),
                4,
              ).map((node: any, index: number) => (
                <button
                  key={index}
                  type="button"
                  style={{
                    fontWeight:
                      pagination.state.page === Number(node.page - 1)
                        ? "bold"
                        : "normal",
                  }}
                  onClick={() =>
                    pagination.fns.onSetPage(Number(node.page - 1))
                  }
                >
                  {node.label}
                </button>
              ))}
              {membersData.nodes.length === 0 && (
                <button
                  key={0}
                  type="button"
                  style={{
                    fontWeight: "normal",
                  }}
                  onClick={() => pagination.fns.onSetPage(0)}
                >
                  {1}
                </button>
              )}
            </span>
          </div>
        </>
      )}
    </>
  );
};
